import { useState } from "react";
import { Heading, Subheading } from "src/components/base/heading";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Badge } from "src/components/base/badge";
import { DocumentTextIcon, CalendarIcon, CurrencyEuroIcon, ArrowDownTrayIcon } from "@heroicons/react/20/solid";
import { StatCard, Card } from "../shared";

const CONTRACT_DATA = [
    {
        id: 1,
        name: "Managed SOC Service v3",
        type: "MSSP",
        status: "Active",
        start: "2024-01-01",
        end: "2026-12-31",
        value: "€45.000/yr",
    },
    {
        id: 2,
        name: "External Pentest - Annual",
        type: "Pentest",
        status: "Active",
        start: "2026-03-01",
        end: "2027-02-28",
        value: "€12.500",
    },
    {
        id: 3,
        name: "Incident Response Retainer",
        type: "Retainer",
        status: "Expired",
        start: "2023-01-01",
        end: "2025-12-31",
        value: "€5.000/yr",
    },
    {
        id: 4,
        name: "Cloud Security Audit",
        type: "Consulting",
        status: "Draft",
        start: "2026-05-01",
        end: "2026-06-01",
        value: "€8.200",
    },
    {
        id: 5,
        name: "ISO 27001 Readiness",
        type: "Compliance",
        status: "Upcoming",
        start: "2026-09-01",
        end: "2027-09-01",
        value: "€18.000",
    },
];

export function ContractDashboard() {
    const [filter, setFilter] = useState<"Active" | "Expired" | "Draft">("Active");

    // Filter Logik: Upcoming wird bei Draft mit angezeigt
    const filteredContracts = CONTRACT_DATA.filter((c) => {
        if (filter === "Draft") return c.status === "Draft" || c.status === "Upcoming";
        return c.status === filter;
    });

    return (
        <div className="space-y-8">
            <div>
                <Heading>Contracts & Subscriptions</Heading>
                <Subheading>Overview of your active service agreements and upcoming renewals.</Subheading>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <StatCard label="Active Contracts" value="2" trend="Next renewal: Dec 2026" />
                <StatCard label="Total Annual Value" value="€57.500" />
                <StatCard label="Pending Drafts" value="2" trend="Awaiting signature" />
            </div>

            {/* Tabs */}
            <div className="flex gap-4 border-b border-zinc-950/10 dark:border-white/10">
                {(["Active", "Expired", "Draft"] as const).map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setFilter(tab)}
                        className={`pb-3 text-sm font-semibold transition-all ${
                            filter === tab
                                ? "border-b-2 border-blue-500 text-blue-600"
                                : "text-zinc-500 hover:text-zinc-700"
                        }`}
                    >
                        {tab === "Draft" ? "Drafts & Upcoming" : tab}
                    </button>
                ))}
            </div>

            <Card className="overflow-hidden p-0">
                <Table dense>
                    <TableHead className="bg-zinc-50 dark:bg-zinc-900/50">
                        <TableRow>
                            <TableHeader>Contract Name</TableHeader>
                            <TableHeader>Service Type</TableHeader>
                            <TableHeader>Period</TableHeader>
                            <TableHeader>Contract Value</TableHeader>
                            <TableHeader>Status</TableHeader>
                            <TableHeader className="text-right">File</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredContracts.length > 0 ? (
                            filteredContracts.map((contract) => (
                                <TableRow
                                    key={contract.id}
                                    className="group transition-colors hover:bg-zinc-50 dark:hover:bg-white/5"
                                >
                                    <TableCell className="font-semibold text-zinc-900 dark:text-white">
                                        <div className="flex items-center gap-2">
                                            <DocumentTextIcon className="h-4 w-4 text-zinc-400" />
                                            {contract.name}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge color="zinc">{contract.type}</Badge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-400">
                                            <CalendarIcon className="h-3.5 w-3.5 opacity-50" />
                                            {contract.start} — {contract.end}
                                        </div>
                                    </TableCell>
                                    <TableCell className="font-mono text-xs font-medium">
                                        <div className="flex items-center gap-1">
                                            <CurrencyEuroIcon className="h-3.5 w-3.5 text-zinc-400" />
                                            {contract.value}
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge
                                            color={
                                                contract.status === "Active"
                                                    ? "green"
                                                    : contract.status === "Expired"
                                                      ? "red"
                                                      : contract.status === "Upcoming"
                                                        ? "blue"
                                                        : "amber"
                                            }
                                        >
                                            {contract.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <button
                                            className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 transition-colors hover:text-blue-500"
                                            title="Download PDF"
                                        >
                                            <ArrowDownTrayIcon className="h-4 w-4" />
                                            PDF
                                        </button>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} className="py-12 text-center italic text-zinc-500">
                                    No contracts found for this category.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Card>

            {/* Note Box für Verlängerungen */}
            {filter === "Active" && (
                <div className="rounded-lg border border-blue-500/20 bg-blue-50/50 p-4 dark:bg-blue-950/10">
                    <div className="flex gap-3">
                        <div className="flex-1 text-sm text-blue-800 dark:text-blue-300">
                            <strong>Notice:</strong> Your{" "}
                            <code className="rounded bg-blue-100 px-1 dark:bg-blue-900/40">Managed SOC Service</code> is
                            set to auto-renew on Dec 31st. If you wish to make changes to your plan, please contact your
                            account manager before Oct 31st.
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
